

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getLoggedInUser } from '../../utils/authHelper';
import {
    IoArrowBack, IoCalendarOutline, IoPersonOutline, IoChatbubblesOutline, IoReorderFourOutline,
    IoPricetagOutline, IoClose, IoBuildOutline, IoMegaphoneOutline, IoPencil, IoHardwareChipOutline,
    IoNotificationsOutline, IoChevronDown, IoSearch, IoHourglassOutline, IoAttach,
    IoDocumentTextOutline, IoTrash, IoCloudUploadOutline, IoMailOutline, IoSaveOutline,
    IoGitNetworkOutline
} from 'react-icons/io5';
import './TaskDetailPage.css';

// --- Configuration from Environment Variables ---
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';

// --- API Helpers ---
const getApiHeaders = (isFormData = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
        headers['Accept'] = 'application/json';
    }
    return headers;
};

const apiFetch = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }
    return response.json();
};

const getCompanyDetails = async () => apiFetch(`${SWAGGER_BASE_URL}/api/get-company`, { headers: getApiHeaders() });
const getTaskById = async (taskId) => apiFetch(`${SWAGGER_BASE_URL}/api/get-task-by-id?task_ID=${taskId}`, { headers: getApiHeaders() });

// --- MODIFIED API HELPER TO HANDLE 400 ERROR ---
const getSubTasks = async (parentTaskId) => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-sub-tasks?parentTaskId=${parentTaskId}`, { headers: getApiHeaders() });

    if (response.ok) {
        const data = await response.json();
        return data.data || [];
    }

    if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.message === "sub task list is empty") {
            return [];
        }
    }
    const errorText = await response.text();
    throw new Error(`Failed to fetch sub-tasks - Status: ${response.status}, Body: ${errorText}`);
};

const updateTask = async (taskId, taskData) => apiFetch(`${SWAGGER_BASE_URL}/api/update-task?task_id=${taskId}`, { method: 'PATCH', headers: getApiHeaders(), body: JSON.stringify(taskData) });
const getTaskHistory = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-task-history`, { headers: getApiHeaders() }); return data.message || []; };
const getUsers = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: getApiHeaders() }); return data.userData || []; };
const getProjects = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-project`, { headers: getApiHeaders() }); return data.projects || []; };
const getProjectsByCustomerId = async (customerId) => apiFetch(`${SWAGGER_BASE_URL}/api/get-projects-by-customer-id?customerId=${customerId}`, { headers: getApiHeaders() });
const getProjectAndCustomerByAssetId = async (assetIds) => {
    if (!assetIds || assetIds.length === 0) return Promise.resolve({ data: null });
    const params = new URLSearchParams();
    assetIds.forEach(id => params.append('assetId', id));
    return apiFetch(`${SWAGGER_BASE_URL}/api/get-project-and-customer-by-asset-id?${params.toString()}`, { headers: getApiHeaders() });
};
const getFullHierarchyTree = async () => apiFetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: getApiHeaders() });
const getAttachments = async (id, moduleName) => apiFetch(`${NODE_BASE_URL}/attachment/api/attachments/${id}/${moduleName}`, { headers: getApiHeaders() });
const deleteAttachment = async (id) => { const response = await fetch(`${NODE_BASE_URL}/attachment/api/attachments/${id}`, { method: 'DELETE', headers: getApiHeaders() }); if (!response.ok && response.status !== 204) { const errorData = await response.json().catch(() => ({})); throw new Error(errorData.message || 'Failed to delete attachment'); } return { success: true }; };
const getReportingManagers = async (userId) => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-reporting-managers-by-userId?userId=${userId}`, { headers: getApiHeaders() }); return data.message || []; };
const updateAttachmentAPI = async (attachmentId, payload) => apiFetch(`${NODE_BASE_URL}/attachment/api/attachments/${attachmentId}`, { method: 'PUT', headers: getApiHeaders(), body: JSON.stringify(payload) });
const getAssets = async () => apiFetch(`${ASSET_BASE_URL}/api/assets/`, { headers: getApiHeaders() });
const getAssetByCode = async (assetCode) => apiFetch(`${ASSET_BASE_URL}/api/assets/by-code/${assetCode}`, { headers: getApiHeaders() });
const getTaggedUsers = async (taskId) => apiFetch(`${SWAGGER_BASE_URL}/api/get-tagged-users?taskId=${taskId}`, { headers: getApiHeaders() });
const getProjectsModuleBoolean = async () => { const data = await apiFetch(`${NODE_BASE_URL}/api/get-column/common_master/module_boolean?module_name=projects`, { headers: getApiHeaders() }); return data?.[0]?.value === true; };

const uploadAttachments = async (taskId, moduleName, files, descriptions) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    descriptions.forEach(desc => formData.append('descriptions[]', desc || ''));
    return apiFetch(`${NODE_BASE_URL}/attachment/api/attachments/${taskId}/${moduleName}`, { method: 'POST', headers: getApiHeaders(true), body: formData });
};

const sendTaskEmailWithAttachment = async (recipients, ccRecipients, bccRecipients, taskId, files) => { const toParams = (recipients || []).map(email => `to=${encodeURIComponent(email)}`).join('&'); const ccParams = (ccRecipients || []).map(email => `cc=${encodeURIComponent(email)}`).join('&'); const bccParams = (bccRecipients || []).map(email => `bcc=${encodeURIComponent(email)}`).join('&'); const allParams = [toParams, ccParams, bccParams, `taskId=${taskId}`].filter(Boolean).join('&'); const endpoint = `${SWAGGER_BASE_URL}/api/send-mail-with-attachment?${allParams}`; const formData = new FormData(); files.forEach(file => { formData.append('attachment', file); }); return apiFetch(endpoint, { method: 'POST', headers: getApiHeaders(true), body: formData }); };
const getColumnData = async (table, column) => apiFetch(`${NODE_BASE_URL}/api/get-column/${table}/${column}`, { headers: getApiHeaders() });
const getStatuses = () => getColumnData('common_master', 'current_status');
const getPriorities = () => getColumnData('common_master', 'priority_in_daily_task');
const getCategories = () => getColumnData('common_master', 'category');
const getCustomers = () => apiFetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: getApiHeaders() });

// --- Constants & Utilities ---
const MESSAGE_TIMEOUT_DURATION = 5000;
const MAX_CHAR_LIMIT = 5000;
const padZero = (num) => String(num).padStart(2, '0');
const parseDateString = (dateString) => { if (!dateString) return null; const correctedDateString = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(dateString) ? `${dateString}Z` : dateString; const date = new Date(correctedDateString); return !isNaN(date.getTime()) ? date : null; };
const formatDateForDisplay = (dateString, includeTime = false) => { const date = parseDateString(dateString); if (!date) return 'N/A'; try { const day = padZero(date.getUTCDate()); const month = padZero(date.getUTCMonth() + 1); const year = date.getUTCFullYear(); if (includeTime) { let hours = date.getUTCHours(); const minutes = padZero(date.getUTCMinutes()); const ampm = hours >= 12 ? 'PM' : 'AM'; hours = hours % 12; hours = hours ? hours : 12; const timeString = `${padZero(hours)}:${minutes} ${ampm}`; return `${day}/${month}/${year}, ${timeString}`; } return `${day}/${month}/${year}`; } catch (e) { return 'N/A'; } };
const formatDateTimeForReport = () => { const now = new Date(); const day = now.getDate(); const month = now.getMonth() + 1; const year = now.getFullYear(); let hours = now.getHours(); const minutes = now.getMinutes(); const seconds = now.getSeconds(); const ampm = hours >= 12 ? 'PM' : 'AM'; hours = hours % 12; hours = hours ? hours : 12; return `${day}/${month}/${year}, ${hours}:${padZero(minutes)}:${padZero(seconds)} ${ampm}`; };
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// --- Hierarchy Helpers ---
const findNodeForUser = (tree, userName) => { for (const node of tree) { if (node.name === userName) { return node; } if (node.children) { const found = findNodeForUser(node.children, userName); if (found) return found; } } return null; };
const getAllSubordinates = (node) => { let subordinates = []; if (!node || !node.children) { return []; } for (const child of node.children) { subordinates.push(child); subordinates = subordinates.concat(getAllSubordinates(child)); } return subordinates; };
const findManagerForUser = (tree, userName) => { for (const managerNode of tree) { if (managerNode.children && managerNode.children.length > 0) { const isDirectReport = managerNode.children.some(child => child.name === userName); if (isDirectReport) return managerNode; const foundManager = findManagerForUser(managerNode.children, userName); if (foundManager) return foundManager; } } return null; };

// --- Project Filtering Helper ---
const filterProjectsForUser = (projects, user, showAll, companyKeyword) => {
    if (!user?.id) { return projects; }
    const userId = user.id;
    if (companyKeyword === 'ZO') { return projects.filter(proj => String(proj.createdBy) === String(userId) || (Array.isArray(proj.assigneeId) && proj.assigneeId.some(id => String(id) === String(userId)))); }
    return showAll ? projects : projects.filter(proj => String(proj.createdBy) === String(userId));
};

// --- Date/Time helpers ---
const allMonths = [{ value: '1', name: 'Jan' }, { value: '2', name: 'Feb' }, { value: '3', name: 'Mar' }, { value: '4', name: 'Apr' }, { value: '5', name: 'May' }, { value: '6', name: 'Jun' }, { value: '7', name: 'Jul' }, { value: '8', name: 'Aug' }, { value: '9', name: 'Sep' }, { value: '10', name: 'Oct' }, { value: '11', name: 'Nov' }, { value: '12', name: 'Dec' }];
const allHours = Array.from({ length: 24 }, (_, i) => padZero(i));
const allMinutes = Array.from({ length: 60 }, (_, i) => padZero(i));

// --- PDF Generation Function ---
const generateTaskSummaryPdf = (taskData, existingAttachments, allAssets = []) => {
    const doc = new jsPDF();
    const { title, task_id, status, priority, description, created_by, assignee_name, created_at, updated_at, due_date, project_name, customer, category, asset_name } = taskData;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(33, 33, 33);

    // Split title to wrap automatically for full visibility
    const fullTitleText = `Task Details: ${title} (#${task_id})`;
    const titleLines = doc.splitTextToSize(fullTitleText, 180);
    doc.text(titleLines, 14, 22);

    // Calculate dynamic Y position based on title lines
    let currentY = 22 + (titleLines.length * 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Report Generated: ${formatDateTimeForReport()}`, 14, currentY);

    currentY += 10;

    const mainData = [['Status', status || 'N/A'], ['Priority', priority || 'N/A'], ['Assignee', assignee_name || 'N/A'], ['Category', (typeof category === 'object' ? category.name : category) || 'N/A'], ['Project', (typeof taskData.project === 'object' ? taskData.project.projectName : project_name) || 'N/A'], ['Customer', customer?.name || 'N/A'], ['Created On', formatDateForDisplay(created_at, true)], ['Last Updated', formatDateForDisplay(updated_at, true)], ['Due Date', formatDateForDisplay(due_date, true)],];

    autoTable(doc, {
        startY: currentY,
        head: [['Field', 'Value']],
        body: mainData,
        theme: 'grid',
        headStyles: { fillColor: '#16a085', textColor: 255, fontStyle: 'bold' },
        styles: { font: 'helvetica', fontSize: 10, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: { 0: { fontStyle: 'bold' } },
    });

    let lastY = doc.lastAutoTable.finalY;
    const taskAssetCodes = asset_name || [];
    const assetsForPdf = allAssets.filter(asset => taskAssetCodes.includes(asset.code));

    if (assetsForPdf.length > 0) {
        lastY += 10;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 33, 33);
        doc.text('Associated Assets', 14, lastY);
        lastY += 3;
        autoTable(doc, {
            startY: lastY,
            head: [['Asset Name', 'Asset Code', 'Registration #', 'Serial #']],
            body: assetsForPdf.map(a => [a.name, a.code, a.reg, a.serial]),
            theme: 'grid',
            headStyles: { fillColor: '#16a085', textColor: 255, fontStyle: 'bold' },
            styles: { font: 'helvetica', fontSize: 10, cellPadding: 2.5 },
            alternateRowStyles: { fillColor: [245, 245, 245] },
        });
        lastY = doc.lastAutoTable.finalY;
    }

    lastY += 15;
    if (description) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 33, 33);
        doc.text('Description', 14, lastY);
        lastY += 8;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        const descriptionLines = doc.splitTextToSize(description, 180);
        doc.text(descriptionLines, 14, lastY);
        lastY += (descriptionLines.length * 5) + 10;
    }

    if (existingAttachments && existingAttachments.length > 0) {
        autoTable(doc, {
            startY: lastY,
            head: [['Attached Files']],
            body: existingAttachments.map(att => [decodeURIComponent(att.file_url.split('/').pop())]),
            theme: 'grid',
            headStyles: { fillColor: '#2c3e50', textColor: 255, fontStyle: 'bold' },
        });
    }
    const pdfBlob = doc.output('blob');
    return new File([pdfBlob], `Task-Summary-${task_id}.pdf`, { type: 'application/pdf' });
};

const urlToFile = async (url) => { try { const response = await fetch(url); if (!response.ok) { throw new Error(`Failed to fetch file: ${response.statusText}`); } const blob = await response.blob(); const fileName = decodeURIComponent(url.split('/').pop()); return new File([blob], fileName, { type: blob.type }); } catch (error) { console.error(`Could not convert URL to file: ${url}`, error); return null; } };

// --- Reusable Sub-Components ---
const SearchableDropdown = ({ options, selectedValues, onChange, placeholder, isMultiSelect = false, labelField = 'displayName', valueField = 'id', disabled = false, error = false, allowClear = false, forcePlaceholder = false }) => { const [isOpen, setIsOpen] = useState(false); const [searchTerm, setSearchTerm] = useState(''); const dropdownRef = useRef(null); const filteredOptions = options.filter(option => option && option[labelField] && typeof option[labelField] === 'string' ? option[labelField].toLowerCase().includes(searchTerm.toLowerCase()) : false); const handleSelect = (optionValue) => { if (isMultiSelect) { const newValues = selectedValues.includes(optionValue) ? selectedValues.filter(v => v !== optionValue) : [...selectedValues, optionValue]; onChange(newValues); } else { onChange(optionValue); setIsOpen(false); } }; const getDisplayValue = () => { if (forcePlaceholder) return placeholder; if (isMultiSelect) { if (!selectedValues || selectedValues.length === 0) return placeholder; if (selectedValues.length === 1) { const selectedOption = options.find(opt => opt[valueField] === selectedValues[0]); return selectedOption ? selectedOption.name : '1 selected'; } return `${selectedValues.length} items selected`; } else { const selectedOption = options.find(opt => String(opt[valueField]) === String(selectedValues)); return selectedOption ? selectedOption[labelField] : placeholder; } }; useEffect(() => { const handleClickOutside = (event) => { if (dropdownRef.current && !dropdownRef.current.contains(event.target)) { setIsOpen(false); } }; document.addEventListener('mousedown', handleClickOutside); return () => document.removeEventListener('mousedown', handleClickOutside); }, []); return (<div className="searchable-dropdown-container" ref={dropdownRef}><button type="button" className={`dropdown-trigger ${error ? 'invalid-field' : ''}`} onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled}><span>{getDisplayValue()}</span><IoChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} /></button>{isOpen && (<div className="dropdown-panel"><div className="dropdown-search-container"><IoSearch className="search-icon" /><input type="text" className="dropdown-search-input" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus /></div><ul className="dropdown-options-list">{!isMultiSelect && allowClear && (<li className="dropdown-option clear-option" onClick={() => handleSelect('')}>— Clear Selection —</li>)}{filteredOptions.length > 0 ? (filteredOptions.map(option => { const isSelected = isMultiSelect ? selectedValues.includes(option[valueField]) : String(selectedValues) === String(option[valueField]); return (<li key={option[valueField]} className={`dropdown-option ${isSelected ? 'selected' : ''}`} onClick={() => handleSelect(option[valueField])}><div className="option-content-wrapper">{isMultiSelect && <input type="checkbox" checked={isSelected} readOnly className="dropdown-checkbox" />}<div className="option-label"><span className="option-label-name">{option.name}</span>{option.details && <span className="option-label-details">{option.details}</span>}</div></div></li>); })) : (<li className="dropdown-option-message">No results found</li>)}</ul></div>)}</div>); };
const CommentItem = ({ commentText, timestamp }) => { if (!commentText || commentText.includes('| reason :')) return null; let author = 'Unknown'; let message = commentText.trim(); const firstColonIndex = commentText.indexOf(':'); if (firstColonIndex > -1) { author = commentText.substring(0, firstColonIndex).trim(); message = commentText.substring(firstColonIndex + 1).trim(); const unwantedSuffixRegex = /: Comment\s*$/; if (unwantedSuffixRegex.test(message)) { message = message.replace(unwantedSuffixRegex, '').trim(); } } else { const byMatch = commentText.match(/(.+) by (.+)/); if (byMatch) { const [, text, byAuthor] = byMatch; message = text.trim(); author = byAuthor.trim(); } } if (!message) return null; return (<div className="comment-item"><IoPersonOutline className="comment-icon" /><div className="comment-content"><p className="comment-body"><strong>{author}</strong>: {message}</p></div><span className="comment-date">{formatDateForDisplay(timestamp, true)}</span></div>); };
const RemarkItem = ({ remarkText, timestamp }) => { if (!remarkText) return null; const match = remarkText.match(/(.+) by (.+)/); const action = match ? match[1] : remarkText; const author = match ? match[2] : 'System'; return (<div className="remark-item"><IoMegaphoneOutline className="remark-icon" /><div className="remark-content"><p className="remark-body"><strong>{author}</strong> reported: "{action.trim()}"</p></div><span className="remark-date">{formatDateForDisplay(timestamp, true)}</span></div>); };
const SystemLogItem = ({ logText, timestamp }) => { if (!logText || !logText.includes('| reason :')) { return null; } const systemMatch = logText.match(/(.+) by (.+) \| reason : (.+)/); if (systemMatch) { const [, action, author, reason] = systemMatch; return (<div className="remark-item system-log"><IoBuildOutline className="remark-icon" /><div className="remark-content"><p className="remark-body"><strong>{author}</strong> {action.toLowerCase().replace("has been", "")}.</p><p className="comment-reason">Reason: {reason}</p></div><span className="remark-date">{formatDateForDisplay(timestamp, true)}</span></div>); } return null; };

// --- Attachment Item with Inline Editing ---
const AttachmentItem = ({ attachment, onDelete, onUpdate, canModify, availableAssignees = [] }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(attachment.description || '');
    const [isSaving, setIsSaving] = useState(false);
    const fileName = decodeURIComponent(attachment.file_url.split('/').pop());
    const uploader = availableAssignees.find(user => String(user.id) === String(attachment.added_by));
    const uploaderName = uploader ? uploader.name : 'Unknown';

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateAttachmentAPI(attachment.id, { description: editText, active: true });
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            console.error("Failed to save description:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditText(attachment.description || '');
        setIsEditing(false);
    };

    return (
        <div className="attachment-item">
            <a href={attachment.file_url} download={fileName} target="_blank" rel="noopener noreferrer" className="attachment-download-link" title={`Download ${fileName}`}>
                <IoDocumentTextOutline className="attachment-icon" />
                <div className="attachment-details">
                    <span className="attachment-filename">{fileName}</span>
                    <span className="attachment-uploader">Added by: {uploaderName}</span>
                </div>
            </a>
            <div className="attachment-description-wrapper">
                {isEditing ? (
                    <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="attachment-description-input"
                        disabled={isSaving}
                        autoFocus
                    />
                ) : (
                    <p className="attachment-description">{attachment.description || <span className="no-content-text">No description</span>}</p>
                )}
            </div>
            <div className="attachment-actions">
                {canModify && (
                    isEditing ? (
                        <>
                            <button onClick={handleSave} className="attachment-action-btn save-btn" disabled={isSaving} title="Save">
                                <IoSaveOutline />
                            </button>
                            <button onClick={handleCancel} className="attachment-action-btn cancel-btn" disabled={isSaving} title="Cancel">
                                <IoClose />
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setIsEditing(true)} className="attachment-action-btn edit-btn" title="Edit description">
                            <IoPencil />
                        </button>
                    )
                )}
                {canModify && (
                    <button onClick={() => onDelete(attachment.id)} className="attachment-delete-btn" title="Delete attachment">
                        <IoTrash />
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Main Page Component ---
const TaskDetailPage = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation(); // Added useLocation hook
    const [task, setTask] = useState(null);
    const [parentTask, setParentTask] = useState(null);
    const [subTasks, setSubTasks] = useState([]);
    const [isLoadingTask, setIsLoadingTask] = useState(true);
    const [taskHistory, setTaskHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(true);
    const [activeTab, setActiveTab] = useState('details');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isEmailScreenOpen, setIsEmailScreenOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isAssigneesManager, setIsAssigneesManager] = useState(false);
    const [hasUpdated, setHasUpdated] = useState(false);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const [availablePriorities, setAvailablePriorities] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableAssignees, setAvailableAssignees] = useState([]);
    const [visibleAssignees, setVisibleAssignees] = useState([]);
    const [availableRecipients, setAvailableRecipients] = useState([]);
    const [availableAssets, setAvailableAssets] = useState([]);
    const [availableProjects, setAvailableProjects] = useState([]);
    const [availableCustomers, setAvailableCustomers] = useState([]);
    const [remainingTime, setRemainingTime] = useState(null);
    const [emailFormData, setEmailFormData] = useState({ recipients: [], ccRecipients: [], bccRecipients: [], selectedAttachmentIds: [] });
    const [manualEmail, setManualEmail] = useState({ to: '', cc: '', bcc: '' });
    const [emailError, setEmailError] = useState({ to: '', cc: '', bcc: '', general: '' });
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [hierarchy, setHierarchy] = useState([]);
    const [canCompleteTask, setCanCompleteTask] = useState(false);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [editFormErrors, setEditFormErrors] = useState({});
    const [isDueDateDirty, setIsDueDateDirty] = useState(false);
    const [dueDateChangeReason, setDueDateChangeReason] = useState('');
    const [dueDateReasonError, setDueDateReasonError] = useState('');
    const [reportingManagers, setReportingManagers] = useState([]);
    const [isFetchingManagers, setIsFetchingManagers] = useState(false);
    const [stagedFiles, setStagedFiles] = useState([]);
    const [stagedFileDescriptions, setStagedFileDescriptions] = useState([]);
    const [showAllProjects, setShowAllProjects] = useState(false);
    const [companyKeyword, setCompanyKeyword] = useState('');
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
    const [allProjects, setAllProjects] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]);
    const [allAssets, setAllAssets] = useState([]);
    const [projectsForEdit, setProjectsForEdit] = useState([]);
    const [isTaggedUser, setIsTaggedUser] = useState(false);
    const [isLoadingTaggedUsers, setIsLoadingTaggedUsers] = useState(true);
    const [isFetchingProjectForAsset, setIsFetchingProjectForAsset] = useState(false);
    const [isFetchingProjectsForCustomer, setIsFetchingProjectsForCustomer] = useState(false);
    const [taskAssetsDetails, setTaskAssetsDetails] = useState([]);
    const [isLoadingAssetDetails, setIsLoadingAssetDetails] = useState(false);

    const isInitialEditModeLoad = useRef(true);

    const isTaskCompleted = (task?.status || '').toLowerCase() === 'completed';
    const canPerformActionsAfterCompletion = isSuperAdmin || isAssigneesManager;
    const canEditTask = !isTaskCompleted || canPerformActionsAfterCompletion;
    const canUploadAttachment = !isTaskCompleted || canPerformActionsAfterCompletion;
    const canModifyAttachment = !isTaskCompleted || canPerformActionsAfterCompletion;

    const fetchAllData = useCallback(async () => {
        if (!taskId) return;
        setIsLoadingTask(true); setIsLoadingHistory(true); setIsLoadingAttachments(true); setIsLoadingTaggedUsers(true);
        setParentTask(null); setSubTasks([]);
        try {
            const numericTaskId = parseInt(taskId, 10);

            const [taskResponse, historyResponse, attachmentsResponse, taggedUsersResponse] = await Promise.all([
                getTaskById(taskId),
                getTaskHistory(),
                getAttachments(taskId, 'attachment_master'),
                getTaggedUsers(taskId).catch(error => {
                    console.error("Could not fetch tagged users. This might be due to a missing API endpoint (404). Proceeding without tagged user data.", error);
                    return { tagged_users: [] };
                })
            ]);

            const currentUser = getLoggedInUser();
            if (currentUser && taggedUsersResponse?.tagged_users) {
                const isUserTagged = taggedUsersResponse.tagged_users.some(user => user.id === currentUser.id);
                setIsTaggedUser(isUserTagged);
            } else {
                setIsTaggedUser(false);
            }

            if (taskResponse && taskResponse.task) {
                const currentTask = taskResponse.task;
                setTask(currentTask);
                setRemainingTime(currentTask.remaining_time || 'N/A');

                if (!currentTask.parent_task_id) {
                    const subTasksResponse = await getSubTasks(currentTask.task_id);
                    setSubTasks(subTasksResponse);
                } else {
                    const parentTaskResponse = await getTaskById(currentTask.parent_task_id).catch(err => {
                        console.error("Could not fetch parent task details:", err);
                        return null;
                    });
                    if (parentTaskResponse?.task) setParentTask(parentTaskResponse.task);
                }
            } else {
                setTask(null);
                throw new Error("Task data could not be loaded.");
            }

            const allHistoryForThisTask = (historyResponse || []).filter(h => h.parentTaskId === numericTaskId || h.taskId === numericTaskId).sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
            setTaskHistory(allHistoryForThisTask);
            setAttachments((attachmentsResponse || []).filter(attachment => attachment.active));
        } catch (error) {
            console.error("Failed to fetch page data:", error);
            setErrorMessage("Could not load task details. " + error.message);
        } finally {
            setIsLoadingTask(false);
            setIsLoadingHistory(false);
            setIsLoadingAttachments(false);
            setIsLoadingTaggedUsers(false);
        }
    }, [taskId]);

    useEffect(() => {
        const user = getLoggedInUser(); setLoggedInUser(user); setIsSuperAdmin(user?.role?.name?.toUpperCase() === 'SUPER_ADMIN');
        const fetchInitialDropdownData = async () => {
            try {
                const results = await Promise.allSettled([getStatuses(), getPriorities(), getCategories(), getUsers(), getProjects(), getCustomers(), getFullHierarchyTree(), getAssets(), getProjectsModuleBoolean(), getCompanyDetails()]);
                const [statusesRes, prioritiesRes, categoriesRes, usersRes, projectsRes, customersRes, hierarchyRes, assetsRes, projectModuleBooleanRes, companyRes] = results;

                let currentCompanyKeyword = '';
                if (companyRes.status === 'fulfilled' && companyRes.value.message) {
                    currentCompanyKeyword = companyRes.value.message[0]?.companyKeyword || '';
                    setCompanyKeyword(currentCompanyKeyword);
                }

                const shouldShowAllProjects = projectModuleBooleanRes.status === 'fulfilled' && projectModuleBooleanRes.value;
                setShowAllProjects(shouldShowAllProjects);

                if (statusesRes.status === 'fulfilled') setAvailableStatuses(statusesRes.value?.map(s => s.value) || []);
                if (prioritiesRes.status === 'fulfilled') setAvailablePriorities(prioritiesRes.value?.map(p => p.value) || []);
                if (categoriesRes.status === 'fulfilled') setAvailableCategories(categoriesRes.value?.map(c => c.value) || []);

                if (usersRes.status === 'fulfilled' && usersRes.value) {
                    setAvailableAssignees(usersRes.value);
                    // FILTER: Only include active users in the available recipients for Email Dropdowns
                    const recipients = (usersRes.value || [])
                        .filter(u => u.email && u.active !== false)
                        .map(u => ({
                            id: u.email,
                            name: u.name,
                            details: `(${u.email})`,
                            displayName: `${u.name} (${u.email})`
                        }));
                    setAvailableRecipients(recipients);
                }

                if (projectsRes.status === 'fulfilled' && projectsRes.value) {
                    const allFetchedProjects = (projectsRes.value || []).map(p => ({
                        ...p,
                        id: String(p.projectId),
                        name: p.projectName,
                        createdBy: p.createdBy,
                        assigneeId: p.assigneeId
                    }));
                    setAllProjects(allFetchedProjects);
                    setAvailableProjects(filterProjectsForUser(allFetchedProjects, user, shouldShowAllProjects, currentCompanyKeyword));
                }

                if (customersRes.status === 'fulfilled') {
                    const formattedCustomers = (customersRes.value || []).filter(c => c.active).map(c => ({ id: String(c.id), name: c.name }));
                    setAllCustomers(formattedCustomers);
                    setAvailableCustomers(formattedCustomers);
                }
                if (hierarchyRes.status === 'fulfilled') setHierarchy(hierarchyRes.value?.tree || []);

                if (assetsRes.status === 'fulfilled' && assetsRes.value) {
                    const formattedAssets = (assetsRes.value || []).map(asset => ({
                        id: asset.id, name: asset.name, code: asset.asset_code || 'N/A', reg: asset.registration_number || 'N/A', serial: asset.serial_number || 'N/A',
                        details: `| Code: ${asset.asset_code || 'N/A'} | Reg: ${asset.registration_number || 'N/A'} | S/N: ${asset.serial_number || 'N/A'}`,
                        displayName: `${asset.name} | Code: ${asset.asset_code || 'N/A'} | Reg: ${asset.registration_number || 'N/A'} | S/N: ${asset.serial_number || 'N/A'}`,
                    }));
                    setAllAssets(formattedAssets);
                    setAvailableAssets(formattedAssets);
                }
            } finally {
                setIsLoadingInitialData(false);
            }
        };
        fetchInitialDropdownData(); fetchAllData();
    }, [taskId, fetchAllData]);

    const fetchReportingManagers = useCallback(async (assigneeId) => {
        if (!assigneeId) return [];

        const applySelfAsManagerFallback = (id) => {
            const assigneeUser = availableAssignees.find(a => String(a.id) === String(id));
            return assigneeUser ? [{ id: String(assigneeUser.id), name: assigneeUser.name, displayName: assigneeUser.name }] : [];
        };

        try {
            const managersData = await getReportingManagers(assigneeId);
            if (managersData && managersData.length > 0) {
                return managersData
                    .map(manager => availableAssignees.find(a => a.email === manager.managerEmail))
                    .filter(Boolean)
                    .map(user => ({ id: String(user.id), name: user.name, displayName: user.name }));
            }
            return applySelfAsManagerFallback(assigneeId);
        } catch (error) {
            console.error("Could not fetch reporting managers, using fallback:", error);
            return applySelfAsManagerFallback(assigneeId);
        }
    }, [availableAssignees]);

    useEffect(() => {
        const initializeEditMode = async () => {
            if (!task || !loggedInUser || availableAssignees.length === 0 || allAssets.length === 0) return;

            setIsDueDateDirty(false);
            setErrorMessage('');
            setEditFormErrors({});
            setDueDateChangeReason('');
            setDueDateReasonError('');

            const sourceTask = task.parent_task_id && parentTask ? parentTask : task;

            const currentAssigneeUser = availableAssignees.find(u => u.name === task.assignee_name);
            const currentAssigneeId = currentAssigneeUser ? String(currentAssigneeUser.id) : '';
            const currentManagerId = task.reporting_manager_id ? String(task.reporting_manager_id) : '';
            const currentManagerUser = availableAssignees.find(u => String(u.id) === currentManagerId);
            const currentProjectName = sourceTask.project_name;
            const currentCustomerId = sourceTask.customer_id ? String(sourceTask.customer_id) : '';

            const currentAssetCodes = task.asset_name || [];
            const currentAssetIds = allAssets.filter(asset => currentAssetCodes.includes(asset.code)).map(asset => asset.id);

            let finalVisibleAssignees = [];
            if (isSuperAdmin) {
                finalVisibleAssignees = [...availableAssignees];
            } else {
                const currentUserNode = findNodeForUser(hierarchy, loggedInUser.name);
                if (currentUserNode) {
                    const subordinateIds = new Set(getAllSubordinates(currentUserNode).map(u => String(u.id)));
                    finalVisibleAssignees = availableAssignees.filter(assignee => String(assignee.id) === String(currentUserNode.id) || subordinateIds.has(String(assignee.id)));
                } else {
                    const self = availableAssignees.find(u => String(u.id) === String(loggedInUser.id));
                    finalVisibleAssignees = self ? [self] : [];
                }
            }
            if (currentAssigneeUser && !finalVisibleAssignees.some(u => String(u.id) === currentAssigneeId)) {
                finalVisibleAssignees.unshift(currentAssigneeUser);
            }

            let finalManagerList = [];
            if (currentAssigneeId) {
                setIsFetchingManagers(true);
                finalManagerList = await fetchReportingManagers(currentAssigneeId);
                setIsFetchingManagers(false);
                if (currentManagerUser && !finalManagerList.some(m => String(m.id) === String(currentManagerId))) {
                    finalManagerList.unshift({ id: String(currentManagerUser.id), name: currentManagerUser.name, displayName: currentManagerUser.name });
                }
            }

            let finalProjectsForEdit = [...availableProjects];
            let currentProject = finalProjectsForEdit.find(p => p.name === currentProjectName);
            if (!currentProject && currentProjectName) {
                const projectFromMasterList = allProjects.find(p => p.name === currentProjectName);
                if (projectFromMasterList) {
                    currentProject = projectFromMasterList;
                    finalProjectsForEdit.unshift(projectFromMasterList);
                }
            }

            const initialEditFormData = {
                title: task.title || '',
                description: task.description || '',
                status: (task.status || '').toLowerCase().replace(/_/g, ' '),
                category: (sourceTask.category?.name) || sourceTask.category || '',
                priority: sourceTask.priority || '',
                assigneeId: currentAssigneeId,
                reportingManagerId: currentManagerId,
                projectId: currentProject ? String(currentProject.id) : '',
                customerId: currentCustomerId,
                assetIds: currentAssetIds,
                reminderDays: task.reminder_days || 1,
                newComment: '',
                dueDay: '', dueMonth: '', dueYear: '', hour: '', minute: '',
            };

            if (task.due_date) {
                const d = parseDateString(task.due_date);
                if (d) {
                    initialEditFormData.dueDay = String(d.getUTCDate());
                    initialEditFormData.dueMonth = allMonths[d.getUTCMonth()].name;
                    initialEditFormData.dueYear = String(d.getUTCFullYear());
                    initialEditFormData.hour = padZero(d.getUTCHours());
                    initialEditFormData.minute = padZero(d.getUTCMinutes());
                }
            }

            setEditFormData(initialEditFormData);
            setVisibleAssignees(finalVisibleAssignees);
            setReportingManagers(finalManagerList);
            setProjectsForEdit(finalProjectsForEdit);
        };

        if (isEditMode) {
            isInitialEditModeLoad.current = true;
            initializeEditMode();
        }
    }, [isEditMode, task, parentTask, loggedInUser, availableAssignees, allProjects, allAssets, hierarchy, isSuperAdmin, fetchReportingManagers, availableProjects]);

    useEffect(() => {
        const handleUserAssigneeChange = async () => {
            if (isInitialEditModeLoad.current) {
                isInitialEditModeLoad.current = false;
                return;
            }

            if (!editFormData.assigneeId) {
                setReportingManagers([]);
                setEditFormData(prev => ({ ...prev, reportingManagerId: '' }));
                return;
            }

            setIsFetchingManagers(true);
            const managers = await fetchReportingManagers(editFormData.assigneeId);
            setReportingManagers(managers);
            setIsFetchingManagers(false);

            setEditFormData(prev => {
                const isCurrentManagerValid = managers.some(m => String(m.id) === String(prev.reportingManagerId));

                if (isCurrentManagerValid) {
                    return prev;
                } else {
                    const newManagerId = managers.length === 1 ? managers[0].id : '';
                    return { ...prev, reportingManagerId: newManagerId };
                }
            });
        };

        if (isEditMode) {
            handleUserAssigneeChange();
        }
    }, [isEditMode, editFormData.assigneeId, fetchReportingManagers]);

    useEffect(() => {
        if (!isEditMode) return;

        const handleCustomerChange = async () => {
            const customerId = editFormData.customerId;
            if (customerId) {
                setIsFetchingProjectsForCustomer(true);
                try {
                    const data = await getProjectsByCustomerId(customerId);
                    const customerProjects = (data.projects || []).map(p => ({
                        ...p,
                        id: String(p.projectId),
                        name: p.projectName,
                    }));
                    setProjectsForEdit(customerProjects);
                } catch (error) {
                    console.error("Failed to fetch projects for customer:", error);
                    setProjectsForEdit([]);
                } finally {
                    setIsFetchingProjectsForCustomer(false);
                }
            } else {
                setProjectsForEdit(availableProjects);
            }
        };

        handleCustomerChange();
    }, [isEditMode, editFormData.customerId, availableProjects]);

    useEffect(() => {
        if (!isEditMode || isInitialEditModeLoad.current) return;

        if (editFormData.customerId || editFormData.projectId) {
            return;
        }

        const assetIds = editFormData.assetIds;
        if (!assetIds || assetIds.length === 0) {
            return;
        }

        const fetchDetailsForAsset = async () => {
            setIsFetchingProjectForAsset(true);
            try {
                const response = await getProjectAndCustomerByAssetId(assetIds);
                if (response && Array.isArray(response.data) && response.data.length > 0 && response.data[0]) {
                    const { custId, projectId } = response.data[0];
                    setEditFormData(prev => ({
                        ...prev,
                        customerId: custId ? String(custId) : prev.customerId,
                        projectId: projectId ? String(projectId) : prev.projectId,
                    }));
                }
            } catch (error) {
                console.error("Failed to fetch project and customer for assets:", error);
            } finally {
                setIsFetchingProjectForAsset(false);
            }
        };

        fetchDetailsForAsset();
    }, [isEditMode, editFormData.assetIds]);


    useEffect(() => { if (!loggedInUser || !task) { setCanCompleteTask(false); return; } if (isSuperAdmin) { setCanCompleteTask(true); return; } if (loggedInUser.name === task.assignee_name) { setCanCompleteTask(true); return; } if (hierarchy.length) { const assigneeManager = findManagerForUser(hierarchy, task.assignee_name); if (assigneeManager && assigneeManager.name === loggedInUser.name) { setCanCompleteTask(true); return; } } setCanCompleteTask(false); }, [loggedInUser, task, hierarchy, isSuperAdmin]);
    useEffect(() => { if (hierarchy.length && loggedInUser && task) { const manager = findManagerForUser(hierarchy, task.assignee_name); setIsAssigneesManager(manager && manager.name === loggedInUser.name); } else { setIsAssigneesManager(false); } }, [hierarchy, loggedInUser, task]);

    useEffect(() => {
        const fetchAssetDetails = async () => {
            if (task && task.asset_name && task.asset_name.length > 0) {
                setIsLoadingAssetDetails(true);
                setTaskAssetsDetails([]);
                try {
                    const assetPromises = task.asset_name.map(code => getAssetByCode(code));
                    const results = await Promise.allSettled(assetPromises);
                    const detailedAssets = results
                        .filter(result => result.status === 'fulfilled' && result.value && !result.value.error)
                        .map(result => result.value);
                    setTaskAssetsDetails(detailedAssets);
                } catch (error) {
                    console.error("Failed to fetch asset details:", error);
                } finally {
                    setIsLoadingAssetDetails(false);
                }
            } else {
                setTaskAssetsDetails([]);
            }
        };

        fetchAssetDetails();
    }, [task]);

    // UPDATED HANDLE GO BACK TO PRESERVE FILTERS/PAGE
    const handleGoBack = () => {
        const previousState = location.state || {};
        navigate('/tasks', {
            state: {
                ...previousState, // Preserve filters and page number from previous location state
                targetTaskId: taskId // Signal the list page to scroll to this task
            }
        });
    };

    const handleCreateSubtask = () => {
        if (!task) return;
        navigate('/tasks/create', { state: { parentTask: task, subtaskOnly: true } });
    };

    const handleAddFilesToStage = useCallback((newFiles) => {
        setStagedFiles(prevFiles => {
            const existingFileNames = new Set(prevFiles.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(nf => !existingFileNames.has(nf.name));
            setStagedFileDescriptions(prevDesc => [...prevDesc, ...Array(uniqueNewFiles.length).fill('')]);
            return [...prevFiles, ...uniqueNewFiles];
        });
    }, []);

    const handleFileChange = (event) => {
        if (event.target.files) { handleAddFilesToStage(Array.from(event.target.files)); }
        event.target.value = null;
    };

    const handleFileDrop = useCallback((e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        if (isUploading || !canUploadAttachment) return;
        if (e.dataTransfer.files?.length > 0) { handleAddFilesToStage(Array.from(e.dataTransfer.files)); e.dataTransfer.clearData(); }
    }, [isUploading, canUploadAttachment, handleAddFilesToStage]);

    const handleUploadStagedFiles = async () => {
        if (stagedFiles.length === 0) return;
        setIsUploading(true); setErrorMessage(''); setSuccessMessage('');
        try {
            await uploadAttachments(taskId, 'attachment_master', stagedFiles, stagedFileDescriptions);
            setSuccessMessage(`${stagedFiles.length} file(s) uploaded successfully.`);
            setStagedFiles([]);
            setStagedFileDescriptions([]);
            await fetchAllData();
        } catch (error) {
            setErrorMessage(`Failed to upload files: ${error.message}. Please try again.`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveStagedFile = (indexToRemove) => {
        setStagedFiles(prev => prev.filter((_, i) => i !== indexToRemove));
        setStagedFileDescriptions(prev => prev.filter((_, i) => i !== indexToRemove));
    };

    const handleStagedDescriptionChange = (index, value) => {
        setStagedFileDescriptions(prev => prev.map((desc, i) => (i === index ? value : desc)));
    };

    const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
    const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); if (!isUploading && canUploadAttachment && e.dataTransfer.items?.length > 0) { setIsDragging(true); } }, [isUploading, canUploadAttachment]);
    const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);

    const handleDeleteAttachment = async (attachmentId) => { if (window.confirm('Are you sure you want to delete this file?')) { setErrorMessage(''); setSuccessMessage(''); try { await deleteAttachment(attachmentId); setSuccessMessage('Attachment deleted successfully.'); await fetchAllData(); } catch (error) { console.error("Failed to delete attachment:", error); setErrorMessage(error.message || 'Failed to delete attachment.'); } } };
    const handleOpenEmailScreen = () => { const assignee = availableAssignees.find(u => u.name === task.assignee_name); setEmailFormData({ recipients: assignee ? [assignee.email] : [], ccRecipients: [], bccRecipients: [], selectedAttachmentIds: [] }); setManualEmail({ to: '', cc: '', bcc: '' }); setEmailError({ to: '', cc: '', bcc: '', general: '' }); setSuccessMessage(''); setErrorMessage(''); setIsEmailScreenOpen(true); };
    const handleCloseEmailScreen = () => setIsEmailScreenOpen(false);
    const handleAddRecipient = (type) => { const email = manualEmail[type].trim(); if (!email) return; if (!isValidEmail(email)) { setEmailError(prev => ({ ...prev, [type]: "Invalid email format." })); return; } const allRecipients = [...emailFormData.recipients, ...emailFormData.ccRecipients, ...emailFormData.bccRecipients]; if (allRecipients.includes(email)) { setEmailError(prev => ({ ...prev, [type]: "Email already added." })); return; } const key = type === 'to' ? 'recipients' : `${type}Recipients`; setEmailFormData(prev => ({ ...prev, [key]: [...prev[key], email] })); setManualEmail(prev => ({ ...prev, [type]: '' })); setEmailError(prev => ({ ...prev, [type]: '' })); };
    const handleRemoveRecipient = (type, email) => { const key = type === 'to' ? 'recipients' : `${type}Recipients`; setEmailFormData(prev => ({ ...prev, [key]: prev[key].filter(r => r !== email) })); };
    const handleSendEmail = async () => { if (emailFormData.recipients.length === 0) { setEmailError(prev => ({ ...prev, general: "Please add at least one 'To' recipient." })); return; } setIsGeneratingPdf(true); setEmailError({ to: '', cc: '', bcc: '', general: '' }); setSuccessMessage('Fetching latest task data...'); try { const latestTaskResponse = await getTaskById(taskId); const latestTask = latestTaskResponse.task; if (!latestTask) throw new Error("Could not get latest task details."); setSuccessMessage('Generating task summary PDF...'); const summaryPdfFile = generateTaskSummaryPdf(latestTask, attachments, availableAssets); setSuccessMessage('Preparing selected attachments...'); const selectedAttachments = attachments.filter(att => emailFormData.selectedAttachmentIds.includes(att.id)); const filePromises = selectedAttachments.map(att => urlToFile(att.file_url)); const selectedFiles = (await Promise.all(filePromises)).filter(Boolean); const allFilesToSend = [summaryPdfFile, ...selectedFiles]; setSuccessMessage('Sending email...'); setIsSendingEmail(true); await sendTaskEmailWithAttachment(emailFormData.recipients, emailFormData.ccRecipients, emailFormData.bccRecipients, taskId, allFilesToSend); setSuccessMessage("Email sent successfully! Returning to task details..."); setTimeout(() => { handleCloseEmailScreen(); setSuccessMessage("Email sent successfully!"); setTimeout(() => setSuccessMessage(''), MESSAGE_TIMEOUT_DURATION); }, 2000); } catch (err) { console.error("Failed to send email:", err); setErrorMessage(err.message || "An unknown error occurred."); } finally { setIsGeneratingPdf(false); setIsSendingEmail(false); } };
    const handleRecipientChange = (type, selectedEmailsFromDropdown) => { const key = type === 'to' ? 'recipients' : `${type}Recipients`; const manuallyAddedEmails = emailFormData[key].filter(email => !availableRecipients.find(rec => rec.id === email)); const newRecipientList = [...new Set([...selectedEmailsFromDropdown, ...manuallyAddedEmails])]; setEmailFormData(prev => ({ ...prev, [key]: newRecipientList })); };
    const renderRecipientInput = (type) => { const typeLabels = { to: 'To:', cc: 'CC (Optional):', bcc: 'BCC (Optional):' }; const recipientList = type === 'to' ? 'recipients' : `${type}Recipients`; const dropdownSelection = emailFormData[recipientList].filter(email => availableRecipients.some(rec => rec.id === email)); return (<div className="form-group"> <label>{typeLabels[type]}</label> <div className="recipient-pills-container"> {(emailFormData[recipientList] || []).map(email => (<div key={email} className="recipient-pill"> <span>{availableRecipients.find(r => r.id === email)?.name || email}</span> <button type="button" onClick={() => handleRemoveRecipient(type, email)}><IoClose /></button> </div>))} </div> <SearchableDropdown options={availableRecipients} selectedValues={dropdownSelection} onChange={(values) => handleRecipientChange(type, values)} placeholder="Select existing users..." isMultiSelect={true} /> <div className="manual-email-entry"> <input type="email" placeholder="Type external email and press Enter..." value={manualEmail[type]} onChange={(e) => { setManualEmail(p => ({ ...p, [type]: e.target.value })); if (emailError[type]) setEmailError(p => ({ ...p, [type]: '' })); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(type); } }} /> <button type="button" onClick={() => handleAddRecipient(type)}>Add</button> </div> {emailError[type] && <span className="error-text">{emailError[type]}</span>} </div>); };

    const handleEnterEditMode = () => {
        setIsEditMode(true);
    };

    const handleExitEditMode = () => {
        setIsEditMode(false);
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;

        let newValue = value;
        if ((name === 'description' || name === 'newComment') && value.length > MAX_CHAR_LIMIT) {
            newValue = value.substring(0, MAX_CHAR_LIMIT);
        }

        if (['dueDay', 'dueMonth', 'dueYear', 'hour', 'minute'].includes(name)) {
            setIsDueDateDirty(true);
        }

        const newFormData = { ...editFormData, [name]: newValue };

        if (name === 'projectId') {
            const project = allProjects.find(p => String(p.id) === String(newValue));
            if (project && project.customerId) {
                newFormData.customerId = String(project.customerId);
            }
        }

        if (name === 'customerId' && value !== editFormData.customerId) {
            newFormData.projectId = '';
        }

        setEditFormData(newFormData);

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingEdit(true);
        setErrorMessage('');

        if (isTaggedUser && !isSuperAdmin) {
            const comments = editFormData.newComment.trim();
            if (!comments) {
                setErrorMessage("As a tagged user, you must add a comment to save.");
                setIsSubmittingEdit(false);
                return;
            }
            try {
                const sourceTask = task.parent_task_id && parentTask ? parentTask : task;

                const payload = {
                    title: task.title,
                    description: task.description,
                    status: task.status,
                    priority: sourceTask.priority,
                    category: (typeof sourceTask.category === 'object' ? sourceTask.category.name : sourceTask.category),
                    assigneeName: task.assignee_name,
                    reportingManagerId: task.reporting_manager_id ? parseInt(task.reporting_manager_id, 10) : null,
                    projectName: sourceTask.project_name,
                    customerId: sourceTask.customer_id ? parseInt(sourceTask.customer_id, 10) : null,
                    assetName: task.asset_name || [],
                    dueDate: task.due_date ? new Date(task.due_date).toISOString() : null,
                    reminderDays: task.reminder_days || null,
                    parentTaskId: task.parent_task_id || 0,
                    comments: comments,
                };

                await updateTask(taskId, payload);
                handleExitEditMode();
                await fetchAllData();
                setHasUpdated(true);
            } catch (err) {
                setErrorMessage(err.message || 'Failed to add comment.');
            } finally {
                setIsSubmittingEdit(false);
            }
            return;
        }

        setEditFormErrors({});
        setDueDateReasonError('');

        const errors = {};
        if (!editFormData.title?.trim()) errors.title = 'Task Title is required.';
        if (!editFormData.assigneeId) errors.assigneeId = 'Assignee is required.';
        if (!editFormData.reportingManagerId) errors.reportingManagerId = 'Reporting Manager is required.';
        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            setErrorMessage('Please correct the highlighted errors.');
            setIsSubmittingEdit(false);
            return;
        }

        const newStatus = editFormData.status.toLowerCase();
        if (companyKeyword === 'ZO' && newStatus === 'completed' && !task.parent_task_id && subTasks.some(st => st.active)) {
            setErrorMessage("Complete all sub-tasks before marking the main task as completed.");
            setIsSubmittingEdit(false);
            return;
        }
        if (newStatus === 'completed' && !canCompleteTask) {
            setErrorMessage("You do not have permission to mark this task as 'Completed'.");
            setIsSubmittingEdit(false);
            return;
        }

        if (isDueDateDirty) {
            const { dueDay, dueMonth, dueYear, hour, minute } = editFormData;
            const allFilled = dueDay && dueMonth && dueYear && hour && minute;
            if ((dueDay || dueMonth || dueYear || hour || minute) && !allFilled) {
                setErrorMessage("To set a due date, please fill all date and time fields.");
                setIsSubmittingEdit(false);
                return;
            }
            if (allFilled) {
                const monthIndex = allMonths.findIndex(m => m.name === dueMonth);
                const ts = Date.UTC(dueYear, monthIndex, dueDay, hour, minute);
                if (isNaN(ts)) {
                    setErrorMessage("The selected date is invalid.");
                    setIsSubmittingEdit(false);
                    return;
                }
                if (ts < Date.now()) {
                    setErrorMessage("The due date cannot be in the past.");
                    setIsSubmittingEdit(false);
                    return;
                }
                if (!dueDateChangeReason.trim()) {
                    setDueDateReasonError('A reason is required to change the due date.');
                    setIsSubmittingEdit(false);
                    return;
                }
            }
        }

        if (companyKeyword === 'ZO' && editFormData.projectId && isDueDateDirty) {
            const project = allProjects.find(p => String(p.id) === String(editFormData.projectId));
            if (project?.endDate) {
                const { dueYear, dueMonth, dueDay, hour, minute } = editFormData;
                if (dueYear && dueMonth && dueDay && hour && minute) {
                    const monthIndex = allMonths.findIndex(m => m.name === dueMonth);
                    const taskDueDate = new Date(Date.UTC(dueYear, monthIndex, dueDay, hour, minute));
                    if (taskDueDate > new Date(project.endDate)) {
                        setErrorMessage(`Due date cannot be after the project's end date (${formatDateForDisplay(project.endDate)}).`);
                        setIsSubmittingEdit(false);
                        return;
                    }
                }
            }
        }

        try {
            let finalDueDateString = null;
            if (isDueDateDirty) {
                const { dueYear, dueMonth, dueDay, hour, minute } = editFormData;
                if (dueYear && dueMonth && dueDay && hour && minute) {
                    const monthIndex = allMonths.findIndex(m => m.name === dueMonth);
                    finalDueDateString = new Date(Date.UTC(dueYear, monthIndex, dueDay, hour, minute)).toISOString();
                }
            } else if (task.due_date) {
                const originalDate = parseDateString(task.due_date);
                if (originalDate) {
                    finalDueDateString = originalDate.toISOString();
                }
            }

            const payload = {
                title: editFormData.title,
                description: editFormData.description,
                status: editFormData.status,
                priority: editFormData.priority,
                category: editFormData.category,
                assigneeName: availableAssignees.find(u => String(u.id) === String(editFormData.assigneeId))?.name || '',
                reportingManagerId: editFormData.reportingManagerId ? parseInt(editFormData.reportingManagerId, 10) : null,
                projectName: allProjects.find(p => String(p.id) === String(editFormData.projectId))?.name || null,
                customerId: editFormData.customerId ? parseInt(editFormData.customerId, 10) : null,
                assetName: (editFormData.assetIds || []).map(id => allAssets.find(a => a.id === id)?.code).filter(Boolean),
                dueDate: finalDueDateString,
                reminderDays: Number(editFormData.reminderDays) || null,
                parentTaskId: task.parent_task_id || 0
            };

            const isSubTask = !!task.parent_task_id;
            if (companyKeyword === 'ZO' && isSubTask && newStatus === 'completed') payload.active = false;
            if (task.parent_task_id) {
                const source = parentTask || task;
                payload.projectName = source.project_name;
                payload.category = (typeof source.category === 'object' ? source.category.name : source.category);
                payload.priority = source.priority;
                payload.customerId = source.customer_id ? parseInt(source.customer_id, 10) : null;
            }

            let comments = editFormData.newComment.trim() ? editFormData.newComment.trim() : '';
            if (isDueDateDirty && dueDateChangeReason) comments += `${comments ? '\n' : ''}(Due date change reason:) ${dueDateChangeReason}`;
            if (comments) payload.comments = comments;

            await updateTask(taskId, payload);
            handleExitEditMode();
            await fetchAllData();
            setHasUpdated(true);
        } catch (err) {
            setErrorMessage(err.message || 'Failed to update task.');
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    const processedComments = React.useMemo(() => { if (isLoadingHistory) return []; const historyOldestFirst = [...taskHistory].sort((a, b) => new Date(a.createdAt || a.updatedAt) - new Date(b.createdAt || b.updatedAt)); const uniqueComments = new Map(); historyOldestFirst.forEach(h => { const ts = h.updatedAt || h.createdAt; const userComments = (h.comments || []).filter(c => c && !c.includes('| reason :') && !c.startsWith('(Due date change reason:)')); userComments.forEach(commentText => { if (!uniqueComments.has(commentText)) { uniqueComments.set(commentText, { text: commentText, timestamp: ts }); } }); }); const finalComments = Array.from(uniqueComments.values()); finalComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); return finalComments.map((c, i) => ({ ...c, id: `comment-${i}-${c.timestamp}` })); }, [taskHistory, isLoadingHistory]);
    const allRemarksAndLogs = React.useMemo(() => { if (isLoadingHistory) return []; const historyOldestFirst = [...taskHistory].sort((a, b) => new Date(a.createdAt || a.updatedAt) - new Date(b.createdAt || b.updatedAt)); const uniqueLogs = new Map(); const uniqueRemarks = new Map(); historyOldestFirst.forEach(h => { const ts = h.updatedAt || h.createdAt; (h.comments || []).filter(c => c && c.includes('| reason :')).forEach(logText => { if (!uniqueLogs.has(logText)) { uniqueLogs.set(logText, { text: logText, timestamp: ts }); } }); (h.remarks || []).filter(r => r).forEach(remarkText => { if (!uniqueRemarks.has(remarkText)) { uniqueRemarks.set(remarkText, { text: remarkText, timestamp: ts }); } }); }); const allItems = [...Array.from(uniqueLogs.values()), ...Array.from(uniqueRemarks.values())]; allItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); return allItems.map((item, index) => { if (item.text.includes('| reason :')) { return <SystemLogItem key={`item-log-${index}-${item.timestamp}`} logText={item.text} timestamp={item.timestamp} />; } else { return <RemarkItem key={`item-remark-${index}-${item.timestamp}`} remarkText={item.text} timestamp={item.timestamp} />; } }); }, [taskHistory, isLoadingHistory]);
    useEffect(() => { if (successMessage || errorMessage) { const timer = setTimeout(() => { setSuccessMessage(''); setErrorMessage(''); }, MESSAGE_TIMEOUT_DURATION); return () => clearTimeout(timer); } }, [successMessage, errorMessage]);

    if (isLoadingTask || isLoadingInitialData || isLoadingTaggedUsers || !loggedInUser) return <div className="task-detail-page"><p className="loading-text">Loading task...</p></div>;

    if (!task) return (<div className="task-detail-page"><div className="page-header"><button onClick={() => navigate('/tasks')} className="back-link-button"><IoArrowBack /> Back</button></div><div className="error-message full-page-error">Task data not found.</div></div>);

    const isUserRole = loggedInUser?.role?.name?.toUpperCase() === 'USER';
    const isSubTask = !!task.parent_task_id;
    const formattedCategories = availableCategories.map(cat => ({ id: cat, displayName: cat, name: cat }));
    const formattedPriorities = availablePriorities.map(p => ({ id: p, displayName: p, name: p }));
    const formattedAssignees = visibleAssignees.map(u => ({ id: String(u.id), displayName: u.name, name: u.name }));
    const filteredStatusesForEdit = availableStatuses.filter(status => (status || '').toLowerCase() !== 'completed' || canCompleteTask).map(s => ({ id: s.toLowerCase().replace(/_/g, ' '), displayName: s.replace(/\b\w/g, l => l.toUpperCase()), name: s.replace(/\b\w/g, l => l.toUpperCase()) }));
    const formattedProjects = projectsForEdit.map(p => ({ ...p, displayName: p.name }));
    const formattedCustomers = availableCustomers.map(c => ({ ...c, displayName: c.name }));
    const isAssigneeDropdownDisabled = isSubmittingEdit || isUserRole;
    const isReportingManagerDropdownDisabled = isSubmittingEdit || isFetchingManagers || !editFormData.assigneeId;
    const isProjectFieldsDisabled = companyKeyword === 'PL' ? false : (companyKeyword === 'ZO' ? isSubTask : isSubTask || isUserRole);
    const isFieldDisabledForTaggedUser = isTaggedUser && !isSuperAdmin;


    if (isEmailScreenOpen) {
        return (
            <div className="task-detail-page email-screen-view">
                <div className="page-header"><button onClick={handleCloseEmailScreen} className="back-link-button"><IoArrowBack /> Back to Task Details</button><div className="header-actions"><button onClick={handleSendEmail} className="button-submit" disabled={isGeneratingPdf || isSendingEmail}>{isGeneratingPdf ? 'Processing...' : isSendingEmail ? 'Sending...' : 'Send Email'}</button></div></div>
                {successMessage && <div className={`inline-message ${isSendingEmail || isGeneratingPdf ? 'info-message' : 'success-message'}`}>{successMessage}</div>}
                {errorMessage && <div className="inline-message error-message">{errorMessage}</div>}
                <div className="email-screen-content"><div className="email-form-panel"><h2>Send Task Details for "{task.title}" #{task.task_id}</h2>{renderRecipientInput('to')}{renderRecipientInput('cc')}{renderRecipientInput('bcc')}{emailError.general && <div className="error-message">{emailError.general}</div>}</div><div className="email-attachments-panel"><h3>Attachments</h3><div className="email-attachments-list"><p className="summary-notice">A Task Summary PDF will be automatically generated and attached.</p>{attachments.map(att => (<div key={att.id} className="attachment-checkbox-item"><input type="checkbox" id={`att-${att.id}`} checked={emailFormData.selectedAttachmentIds.includes(att.id)} onChange={() => { const newSelection = emailFormData.selectedAttachmentIds.includes(att.id) ? emailFormData.selectedAttachmentIds.filter(id => id !== att.id) : [...emailFormData.selectedAttachmentIds, att.id]; setEmailFormData(prev => ({ ...prev, selectedAttachmentIds: newSelection })); }} /><label htmlFor={`att-${att.id}`}>{decodeURIComponent(att.file_url.split('/').pop())}</label></div>))}</div></div></div>
            </div>
        );
    }

    if (isEditMode) {
        return (
            <div className="task-detail-page">
                <div className="page-header"><button onClick={handleExitEditMode} className="back-link-button"><IoArrowBack /> Back to Task Details</button></div>
                {errorMessage && <div className="inline-message error-message">{errorMessage}</div>}

                {parentTask && (
                    <div className="subtask-info-banner" style={{ marginBottom: '24px' }}>
                        <IoGitNetworkOutline />
                        <span>This is a subtask of: <Link to={`/tasks/${parentTask.task_id}`}>{parentTask.title} (#{parentTask.task_id})</Link></span>
                    </div>
                )}
                {isFieldDisabledForTaggedUser && (
                    <div className="info-banner" style={{ marginBottom: '24px' }}>
                        You are tagged in this task. You can only add a comment.
                    </div>
                )}

                <form onSubmit={handleEditSubmit} className="edit-task-form" noValidate>
                    <div className="form-main-panel">
                        <div className="form-section">
                            <h2>Task Information</h2>
                            <div className="form-group"><label>Category</label><SearchableDropdown options={formattedCategories} selectedValues={editFormData.category || ''} onChange={(v) => handleEditFormChange({ target: { name: 'category', value: v } })} placeholder="Select Category" disabled={isProjectFieldsDisabled || isFieldDisabledForTaggedUser} /></div>
                            <div className="form-group"><label>Title <span className="required-asterisk">*</span></label><input type="text" name="title" value={editFormData.title || ''} onChange={handleEditFormChange} required className={`form-input ${editFormErrors.title ? 'invalid-field' : ''}`} disabled={isFieldDisabledForTaggedUser} />{editFormErrors.title && <span className="error-text">{editFormErrors.title}</span>}</div>
                            <div className="form-group"><label>Description (Max {MAX_CHAR_LIMIT} characters)</label><textarea name="description" rows="4" value={editFormData.description || ''} onChange={handleEditFormChange} className="form-input" disabled={isFieldDisabledForTaggedUser} /><span className="char-count-display">{editFormData.description?.length || 0} / {MAX_CHAR_LIMIT}</span></div>
                            <div className="form-group"><label>Customer (Optional)</label><SearchableDropdown options={formattedCustomers} selectedValues={editFormData.customerId || ''} onChange={(v) => handleEditFormChange({ target: { name: 'customerId', value: v } })} placeholder="Select Customer" allowClear={!isProjectFieldsDisabled} disabled={isProjectFieldsDisabled || isFieldDisabledForTaggedUser || isFetchingProjectForAsset} /></div>
                            <div className="form-group"><label>Project (Optional)</label><SearchableDropdown options={formattedProjects} selectedValues={editFormData.projectId || ''} onChange={(v) => handleEditFormChange({ target: { name: 'projectId', value: v } })} placeholder={isFetchingProjectsForCustomer ? "Loading..." : "Select Project"} allowClear={!isProjectFieldsDisabled} disabled={isProjectFieldsDisabled || isFieldDisabledForTaggedUser || isFetchingProjectsForCustomer || isFetchingProjectForAsset} /></div>
                            <div className="form-group"><label>Assets (Optional)</label><SearchableDropdown options={availableAssets} selectedValues={editFormData.assetIds || []} onChange={(v) => handleEditFormChange({ target: { name: 'assetIds', value: v } })} placeholder="Select Assets" isMultiSelect={true} disabled={isFieldDisabledForTaggedUser || isFetchingProjectForAsset} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Status</label><SearchableDropdown options={filteredStatusesForEdit} selectedValues={editFormData.status || ''} onChange={(v) => handleEditFormChange({ target: { name: 'status', value: v } })} placeholder="Select Status" disabled={isFieldDisabledForTaggedUser} /></div>
                                <div className="form-group"><label>Priority</label><SearchableDropdown options={formattedPriorities} selectedValues={editFormData.priority || ''} onChange={(v) => handleEditFormChange({ target: { name: 'priority', value: v } })} placeholder="Select Priority" disabled={isProjectFieldsDisabled || isFieldDisabledForTaggedUser} /></div>
                            </div>
                            <div className="form-group"><label>Add Comment (optional, Max {MAX_CHAR_LIMIT} characters)</label><textarea name="newComment" rows="3" value={editFormData.newComment || ''} onChange={handleEditFormChange} placeholder="Add a comment to log with this update..." className="form-input" /><span className="char-count-display">{editFormData.newComment?.length || 0} / {MAX_CHAR_LIMIT}</span></div>
                        </div>
                    </div>
                    <div className="form-side-panel">
                        <div className="form-section">
                            <h2>Assignment</h2>
                            <div className="form-group"><label>Assignee <span className="required-asterisk">*</span></label><SearchableDropdown options={formattedAssignees} selectedValues={editFormData.assigneeId || ''} onChange={(v) => handleEditFormChange({ target: { name: 'assigneeId', value: v } })} placeholder="Select Assignee" disabled={isAssigneeDropdownDisabled || isFieldDisabledForTaggedUser} error={!!editFormErrors.assigneeId} />{editFormErrors.assigneeId && <span className="error-text">{editFormErrors.assigneeId}</span>}</div>
                            <div className="form-group"><label>Reporting Manager <span className="required-asterisk">*</span></label><SearchableDropdown options={reportingManagers} selectedValues={editFormData.reportingManagerId || ''} onChange={(v) => handleEditFormChange({ target: { name: 'reportingManagerId', value: v } })} placeholder={isFetchingManagers ? "Loading..." : "Select Manager"} disabled={isReportingManagerDropdownDisabled || isFieldDisabledForTaggedUser} allowClear={true} error={!!editFormErrors.reportingManagerId} />{editFormErrors.reportingManagerId && <span className="error-text">{editFormErrors.reportingManagerId}</span>}</div>
                        </div>
                        <div className="form-section">
                            <h2>Due Date</h2>
                            <div className="form-group"><label>Due Date & Time</label><div className="duedate-inputs"><div className="date-picker-dmy"><select name="dueDay" value={editFormData.dueDay || ''} onChange={handleEditFormChange} disabled={isFieldDisabledForTaggedUser}><option value="">Day</option>{Array.from({ length: 31 }, (_, i) => String(i + 1)).map(d => <option key={`day-${d}`} value={d}>{d}</option>)}</select><select name="dueMonth" value={editFormData.dueMonth || ''} onChange={handleEditFormChange} disabled={isFieldDisabledForTaggedUser}><option value="">Month</option>{allMonths.map(m => <option key={`month-${m.value}`} value={m.name}>{m.name}</option>)}</select><select name="dueYear" value={editFormData.dueYear || ''} onChange={handleEditFormChange} disabled={isFieldDisabledForTaggedUser}><option value="">Year</option>{Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(y => <option key={`year-${y}`} value={y}>{y}</option>)}</select></div><div className="time-picker-24h"><select name="hour" value={editFormData.hour || ''} onChange={handleEditFormChange} disabled={isFieldDisabledForTaggedUser}><option value="">HH</option>{allHours.map(h => <option key={`hour-${h}`} value={h}>{h}</option>)}</select><span>:</span><select name="minute" value={editFormData.minute || ''} onChange={handleEditFormChange} disabled={isFieldDisabledForTaggedUser}><option value="">MM</option>{allMinutes.map(m => <option key={`min-${m}`} value={m}>{m}</option>)}</select></div></div></div>
                            {isDueDateDirty && !isFieldDisabledForTaggedUser && (<div className="form-group"><label>Reason for Due Date Change <span className="required-asterisk">*</span></label><textarea value={dueDateChangeReason} onChange={(e) => { setDueDateChangeReason(e.target.value); if (dueDateReasonError) setDueDateReasonError(''); }} className={`form-input ${dueDateReasonError ? 'invalid-field' : ''}`} rows="2" placeholder="Please provide a reason..." required />{dueDateReasonError && <span className="error-text">{dueDateReasonError}</span>}</div>)}
                        </div>
                    </div>
                    <div className="form-actions"><button type="submit" className="button-submit" disabled={isSubmittingEdit}>{isSubmittingEdit ? 'Saving...' : 'Save Changes'}</button></div>
                </form>
            </div>
        );
    }

    return (
        <div className="task-detail-page">
            <div className="page-header">
                <button onClick={handleGoBack} className="back-link-button"><IoArrowBack /> Back to Tasks</button>
                <div className="header-actions">
                    {companyKeyword === 'ZO' && !task.parent_task_id && (!isTaskCompleted || canPerformActionsAfterCompletion) && (
                        <button onClick={handleCreateSubtask} className="action-btn subtask-btn" title="Create Subtask"><IoGitNetworkOutline /> Create Subtask</button>
                    )}
                    {isTaskCompleted && (
                        <button onClick={handleOpenEmailScreen} className="action-btn email-btn" disabled={isSendingEmail || isGeneratingPdf} title="Send Task Details via Email"><IoMailOutline /> Send Email</button>
                    )}
                    <button className="edit-task-btn" onClick={handleEnterEditMode} disabled={!canEditTask || isLoadingTask || isLoadingInitialData}><IoPencil /> Edit Task</button>
                </div>
            </div>
            {successMessage && <div className={`inline-message ${isSendingEmail || isGeneratingPdf ? 'info-message' : 'success-message'}`}>{successMessage}</div>}
            {errorMessage && <div className="inline-message error-message">{errorMessage}</div>}

            <div className="task-header-section">
                <h1>{task.title} <span className="task-id-display">#{taskId}</span></h1>
                <div className="task-meta-tags">
                    {task.status && <span className={`task-badge status-${(task.status || '').toLowerCase().replace(/_/g, '-')}`}>{task.status.replace(/_/g, ' ')}</span>}
                    {task.priority && <span className={`task-badge priority-${(task.priority || '').toLowerCase()}`}>{task.priority}</span>}
                    {(isTaskCompleted || (task.status || '').toLowerCase() === 'completed') && (task.completed_at || task.completedAt) && (
                        <span className="completion-date-badge">
                            <IoCalendarOutline /> Completed on: {formatDateForDisplay(task.completed_at || task.completedAt, true)}
                        </span>
                    )}
                </div>
            </div>

            {parentTask && (
                <div className="subtask-info-banner">
                    <IoGitNetworkOutline />
                    <span>This is a subtask of: <Link to={`/tasks/${parentTask.task_id}`}>{parentTask.title} (#{parentTask.task_id})</Link></span>
                </div>
            )}

            <div className="task-content-layout">
                <main className="task-main-content">
                    <div className="task-tabs">
                        <button className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}><IoReorderFourOutline /> Details</button>
                        <button className={`tab-btn ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')}><IoChatbubblesOutline /> Comments</button>
                        <button className={`tab-btn ${activeTab === 'remarks' ? 'active' : ''}`} onClick={() => setActiveTab('remarks')}><IoMegaphoneOutline /> Remarks & Activity</button>
                        <button className={`tab-btn ${activeTab === 'attachments' ? 'active' : ''}`} onClick={() => setActiveTab('attachments')}><IoAttach /> Attachments</button>
                    </div>
                    <div className="tab-content">
                        {activeTab === 'details' && (<section><h2>Description</h2><p className="task-description-text">{task.description || <span className="no-content-text">No description provided.</span>}</p></section>)}
                        {activeTab === 'comments' && (<section><h2>Comments</h2>{isLoadingHistory ? <p>Loading...</p> : processedComments.length > 0 ? (<div className="comments-list">{processedComments.map(c => <CommentItem key={c.id} commentText={c.text} timestamp={c.timestamp} />)}</div>) : <p className="no-content-text">No comments yet.</p>}</section>)}
                        {activeTab === 'remarks' && (<section><h2>Remarks & Activity Log</h2>{isLoadingHistory ? <p>Loading...</p> : allRemarksAndLogs.length > 0 ? (<div className="remarks-list">{allRemarksAndLogs}</div>) : <p className="no-content-text">No remarks or activity yet.</p>}</section>)}
                        {activeTab === 'attachments' && (
                            <section>
                                <h2>Attachments</h2>
                                {isLoadingAttachments ? <p>Loading...</p> : attachments.length > 0 ? (<div className="attachments-list">{attachments.map((att) => <AttachmentItem key={att.id} attachment={att} onDelete={handleDeleteAttachment} onUpdate={fetchAllData} canModify={canModifyAttachment} availableAssignees={availableAssignees} />)}</div>) : <p className="no-content-text">No attachments found.</p>}
                                <div className="upload-section-container">
                                    <h3>Upload New Attachment</h3>
                                    <label htmlFor="page-file-input" className={`attachment-upload-area ${!canUploadAttachment ? 'disabled' : ''} ${isDragging ? 'drag-over' : ''}`} onDrop={handleFileDrop} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}>
                                        {canUploadAttachment ? (<><div className="file-upload-content"><IoCloudUploadOutline /><span>{isDragging ? 'Drop files to upload' : 'Drag & Drop or Click to Upload'}</span></div><input id="page-file-input" type="file" onChange={handleFileChange} disabled={isUploading} className="file-upload-hidden" multiple /></>) : <p className="no-content-text">Uploading disabled for completed tasks.</p>}
                                    </label>
                                    {stagedFiles.length > 0 && (
                                        <div className="staged-files-section">
                                            <h4>Files to Upload</h4>
                                            <div className="staged-files-list">{stagedFiles.map((file, index) => (<div key={file.name} className="staged-file-item"><span className="staged-file-name" title={file.name}>{file.name}</span><input type="text" placeholder="Add description..." value={stagedFileDescriptions[index]} onChange={(e) => handleStagedDescriptionChange(index, e.target.value)} className="staged-file-description-input" disabled={isUploading} /><button onClick={() => handleRemoveStagedFile(index)} className="remove-staged-file-btn" disabled={isUploading}><IoClose /></button></div>))}</div>
                                            <button onClick={handleUploadStagedFiles} className="button-submit upload-staged-btn" disabled={isUploading}>{isUploading ? 'Uploading...' : `Upload ${stagedFiles.length} File(s)`}</button>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}
                    </div>
                </main>
                <aside className="task-sidebar-details">
                    <div className="details-card">
                        <h3>Details</h3>
                        <ul>
                            <li><IoCalendarOutline /><div><span>Created On</span><strong>{formatDateForDisplay(task.created_at, true)}</strong></div></li>
                            {task.updated_at && new Date(task.updated_at) > new Date(task.created_at) && (<li><IoCalendarOutline /><div><span>Last Updated</span><strong>{formatDateForDisplay(task.updated_at, true)}</strong></div></li>)}
                            <li><IoCalendarOutline /><div><span>Due Date</span><strong>{formatDateForDisplay(task.due_date, true)}</strong></div></li>
                            {!isTaskCompleted && remainingTime && (<li className={remainingTime.includes('Overdue') ? 'detail-overdue' : ''}><IoHourglassOutline /><div><span>Time Remaining</span><strong>{remainingTime}</strong></div></li>)}
                            {task.reminder_days > 0 && <li><IoNotificationsOutline /><div><span>Reminder</span><strong>{task.reminder_days} day(s) before due</strong></div></li>}
                            <li><IoPersonOutline /><div><span>Created By</span><strong>{task.created_by || 'N/A'}</strong></div></li>
                            <li><IoPersonOutline /><div><span>Assigned To</span><strong>{task.assignee_name || 'N/A'}</strong></div></li>
                            {task.reporting_manager_name && <li><IoPersonOutline /><div><span>Reporting Manager</span><strong>{task.reporting_manager_name}</strong></div></li>}
                            {task.category && <li><IoPricetagOutline /><div><span>Category</span><strong>{typeof task.category === 'object' ? task.category.name : task.category}</strong></div></li>}
                            {(task.project_name || task.project) && <li><IoPricetagOutline /><div><span>Project</span><strong>{(typeof task.project === 'object' ? task.project.projectName : task.project) || task.project_name}</strong></div></li>}
                            {task.asset_name && task.asset_name.length > 0 && (
                                <li>
                                    <IoHardwareChipOutline />
                                    <div>
                                        <span>Assets</span>
                                        <div className="asset-list-sidebar">
                                            {isLoadingAssetDetails ? (
                                                <strong>Loading asset details...</strong>
                                            ) : taskAssetsDetails.length > 0 ? (
                                                taskAssetsDetails.map((asset, index) => (
                                                    <strong key={index}>{asset.name} ({asset.asset_code})</strong>
                                                ))
                                            ) : (
                                                task.asset_name.map((assetCode, index) => (
                                                    <strong key={index}>{assetCode}</strong>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </li>
                            )}
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default TaskDetailPage;